import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CardComponent } from '@app/components/card';
import { ChipColor, ChipComponent, ChipSize } from '@app/components/chip';
import { InputComponent } from '@app/components/input';
import {
  OptionsObj,
  SelectDropdownComponent,
} from '@app/components/select-dropdown';
import { SpinnerComponent } from '@app/components/spinner';
import { TextComponent } from '@app/components/text';
import { TitleComponent, TitleSpacing } from '@app/components/title-section';
import { ToastService, ToastType } from '@app/components/toast';
import { ToggleComponent } from '@app/components/toggle';
import {
  Challenge,
  ChallengeService,
  ChallengeTeam,
  getMetricChallengeStatus,
  MetricChallengeViewStatus,
  STEP_CHALLENGE_TEAM_ASSIGNMENT_OPTIONS,
  StepChallengeTeamAssignmentType,
} from '@app/services/challenge/challenge.service';
import { Logger } from '@app/services/logger/logger.service';
import { TranslatorService } from '@app/services/translator/translator.service';
import { TranslatePipe } from '@ngx-translate/core';
import { catchError, forkJoin, of } from 'rxjs';
import { ChallengeLeaderboardCardComponent } from '../../challenge-live-details/challenge-leaderboard-card/challenge-leaderboard-card.component';

@Component({
  selector: 'app-step-challenge-view',
  templateUrl: './step-challenge-view.component.html',
  styleUrls: ['./step-challenge-view.component.css'],
  standalone: true,
  imports: [
    CardComponent,
    ChipComponent,
    CommonModule,
    InputComponent,
    ReactiveFormsModule,
    SpinnerComponent,
    TextComponent,
    TitleComponent,
    ToggleComponent,
    TranslatePipe,
    SelectDropdownComponent,
    ChallengeLeaderboardCardComponent,
  ],
})
export class StepChallengeViewComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly translatorService = inject(TranslatorService);
  private readonly logger = inject(Logger);
  private readonly toastService = inject(ToastService);
  private readonly challengeService = inject(ChallengeService);
  private readonly destroyRef = inject(DestroyRef);

  isLoading = signal(true);
  isCanJoinAfterStart = signal(false);
  teamAssignmentType = signal(StepChallengeTeamAssignmentType.DEFAULT);
  stepChallengeTeamAssignmentType = StepChallengeTeamAssignmentType;
  readonly teamAssignmentOptions = STEP_CHALLENGE_TEAM_ASSIGNMENT_OPTIONS;
  challenge = signal<Challenge | null>(null);
  challengeStatus = signal<MetricChallengeViewStatus>(
    MetricChallengeViewStatus.NOT_SET
  );
  challengeDates = signal('');
  TitleSpacing = TitleSpacing;
  ChipColor = ChipColor;
  ChipSize = ChipSize;
  MetricChallengeViewStatus = MetricChallengeViewStatus;

  isResultsView = computed(() => {
    const status = this.challengeStatus();
    return (
      status === MetricChallengeViewStatus.STARTED ||
      status === MetricChallengeViewStatus.ENDED
    );
  });
  isLiveChallenge = computed(
    () => this.challengeStatus() === MetricChallengeViewStatus.STARTED
  );
  teams = computed(() =>
    (this.challenge()?.teams ?? []).map((team) => this.toLeaderboardTeam(team))
  );

  stepChallengeForm = new FormGroup({
    title: new FormControl({ value: '', disabled: true }),
    description: new FormControl({ value: '', disabled: true }),
    duration: new FormControl({ value: 1, disabled: true }),
    durationUnit: new FormControl({ value: 1, disabled: true }),
    dailyStepLimit: new FormControl({ value: 'unlimited', disabled: true }),
    endDate: new FormControl<string | null>({ value: null, disabled: true }),
    signupDate: new FormControl<string | null>({ value: null, disabled: true }),
    startDate: new FormControl<string | null>({ value: null, disabled: true }),
    numberOfTeams: new FormControl({ value: '', disabled: true }),
    maxTeamNumber: new FormControl({ value: '', disabled: true }),
  });
  durationUnits = [
    { label: 'Days', value: 1 },
    { label: 'Weeks', value: 7 },
  ];
  dailyStepLimitOptions: OptionsObj[] = [
    { label: 'Unlimited', value: 'unlimited' },
    { label: '1000', value: '1k/day' },
    { label: '2500', value: '2.5k/day' },
    { label: '5000', value: '5k/day' },
    { label: '7500', value: '7.5k/day' },
    { label: '10000', value: '10k/day' },
  ];

  goBack() {
    this.router.navigate(['challenges']);
  }

  getAssignmentDataTestId(type: StepChallengeTeamAssignmentType): string {
    return `step-challenge-view-team-assignment-option-${type}-toggle`;
  }

  leaderboardHref(team: ChallengeTeam): string {
    const challengeId = this.challenge()?.id;
    const teamId = this.challengeService.getTeamId(team);
    return `/challenges/steps/${challengeId}/leaderboard/${teamId}`;
  }

  private deriveDuration(startDate: string, endDate: string): number[] {
    return this.challengeService.dateToDurationUnit(startDate, endDate);
  }

  private toDateString(date?: string): string | null {
    if (!date) {
      return null;
    }
    const parsedDate = new Date(date);
    return isNaN(parsedDate.getTime())
      ? null
      : parsedDate.toISOString().split('T')[0];
  }

  // Challenge dates are calendar dates parsed as UTC midnight, so format them in
  // UTC; local time would show the previous day in time zones behind UTC.
  private formatDateRange(startDate: Date, endDate: Date): string {
    const locale = 'en-US';
    const monthFormat = 'short';
    const utcTimeZone = 'UTC';
    const options: Intl.DateTimeFormatOptions = {
      month: monthFormat,
      timeZone: utcTimeZone,
    };
    const startMonth = startDate.toLocaleString(locale, options);
    const endMonth = endDate.toLocaleString(locale, options);
    return `${startMonth} ${startDate.getUTCDate()} - ${endMonth} ${endDate.getUTCDate()}, ${endDate.getUTCFullYear()}`;
  }

  private toLeaderboardTeam(team: ChallengeTeam): ChallengeTeam {
    return {
      name: team.name,
      score: team.score ?? 0,
      rank: team.rank ?? 0,
      color: team.color ?? '#1100DB',
      teamId: team.teamId ?? team.id,
      members: team.members ?? [],
    } as ChallengeTeam;
  }

  private populateForm(challenge: Challenge): void {
    const startDate = this.toDateString(challenge.startDate);
    const endDate = this.toDateString(challenge.endDate);
    const [duration, durationUnit] =
      startDate && endDate ? this.deriveDuration(startDate, endDate) : [1, 1];
    const teamAssignmentType = this.toTeamAssignmentType(challenge);
    const assignmentValue =
      challenge.teamAssignmentValue != null
        ? String(challenge.teamAssignmentValue)
        : '';
    this.isCanJoinAfterStart.set(!!challenge.joinInProgress);
    this.teamAssignmentType.set(teamAssignmentType);
    this.stepChallengeForm.patchValue({
      title: challenge.name ?? '',
      description: challenge.description ?? '',
      duration,
      durationUnit,
      dailyStepLimit: challenge.goal ?? 'unlimited',
      signupDate: this.toDateString(challenge.enrollmentStartDate),
      startDate,
      endDate,
      numberOfTeams:
        teamAssignmentType === StepChallengeTeamAssignmentType.FIXED_TEAM_COUNT
          ? assignmentValue
          : '',
      maxTeamNumber:
        teamAssignmentType === StepChallengeTeamAssignmentType.MAX_TEAM_SIZE
          ? assignmentValue
          : '',
    });
  }

  private toTeamAssignmentType(
    challenge: Challenge
  ): StepChallengeTeamAssignmentType {
    if (
      challenge.teamAssignmentType ===
      StepChallengeTeamAssignmentType.FIXED_TEAM_COUNT
    ) {
      return StepChallengeTeamAssignmentType.FIXED_TEAM_COUNT;
    }
    if (
      challenge.teamAssignmentType ===
      StepChallengeTeamAssignmentType.MAX_TEAM_SIZE
    ) {
      return StepChallengeTeamAssignmentType.MAX_TEAM_SIZE;
    }
    return StepChallengeTeamAssignmentType.DEFAULT;
  }

  private applyChallenge(challenge: Challenge): void {
    this.challenge.set(challenge);
    const startDate = challenge.startDate
      ? new Date(challenge.startDate)
      : undefined;
    const endDate = challenge.endDate ? new Date(challenge.endDate) : undefined;
    if (
      startDate &&
      endDate &&
      !Number.isNaN(startDate.getTime()) &&
      !Number.isNaN(endDate.getTime())
    ) {
      this.challengeStatus.set(getMetricChallengeStatus(startDate, endDate));
      this.challengeDates.set(this.formatDateRange(startDate, endDate));
    }
    this.populateForm(challenge);
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.logger.error('Missing challenge ID');
      this.toastService.add(
        this.translatorService.translate(
          'challenges.challengeCouldNotBeLoaded'
        ),
        ToastType.ERROR
      );
      this.isLoading.set(false);
      this.goBack();
      return;
    }

    forkJoin({
      challenge: this.challengeService.getStepChallenge(id),
      teams: this.challengeService.getChallengeTeamsWithMembers(id).pipe(
        catchError((err) => {
          this.logger.error('Error loading challenge teams');
          this.logger.error(JSON.stringify(err, null, 2));
          return of([]);
        })
      ),
    })
      .pipe(
        catchError((err) => {
          this.logger.error('Error loading challenge');
          this.logger.error(JSON.stringify(err, null, 2));
          this.toastService.add(
            this.translatorService.translate(
              'challenges.challengeCouldNotBeLoaded'
            ),
            ToastType.ERROR
          );
          this.goBack();
          return of({ challenge: null, teams: [] });
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ challenge, teams }) => {
        this.isLoading.set(false);
        if (challenge) {
          this.applyChallenge({ ...challenge, teams });
        }
      });
  }
}
